import React, { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import { getEpic } from 'service/project/api/epic';
import { epicQueryKey, useMarkEpicDoneMutation } from 'service/project/hooks/useEpicQueries';
import { WorkStatusCategory } from 'types/project/projectDefaults';

/**
 * After a work item is moved to Done, offers to mark its Epic as Done too when
 * that was the Epic's last open child. Only offers: the Epic's status is the
 * user's to set.
 *
 * Call the returned function once the status change has been saved.
 */
export const useEpicDonePrompt = () => {
  const queryClient = useQueryClient();
  const { mutate: markEpicDone } = useMarkEpicDoneMutation();

  return useCallback(
    async (projectUuid: string, epicUuid?: string | null) => {
      if (!projectUuid || !epicUuid) return;
      // Same key as the Epic page's default view, so its cache is refreshed too.
      const queryKey = [...epicQueryKey(epicUuid), { hideDone: true }];
      try {
        // A read already in flight may predate this save (e.g. when several children
        // are saved at once), so it is dropped and the counts read afresh. The
        // dropped read's own prompt is skipped; the latest save's prompt decides.
        await queryClient.cancelQueries({ queryKey, exact: true });
        const { epic } = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => getEpic(projectUuid, epicUuid, true),
          staleTime: 0,
        });
        const isLastChildDone =
          epic.totalCount > 0 && epic.openCount === 0 && epic.workStatusCategory !== WorkStatusCategory.DONE;
        if (!isLastChildDone) return;

        toast(
          (t) => (
            <div className="flex flex-col gap-2.5">
              <p>
                All child work items of <span className="font-semibold">{epic.title}</span> are done. Mark this
                Epic as Done?
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => toast.dismiss(t.id)}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-neutral-medium hover:text-neutral"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={() => {
                    toast.dismiss(t.id);
                    markEpicDone({ projectUuid, epicUuid });
                  }}
                  className="rounded-md bg-[#10B981] px-3 py-1.5 text-xs font-semibold text-[#052E1C] hover:bg-[#34D399]"
                >
                  Mark as Done
                </button>
              </div>
            </div>
          ),
          { id: `epic-done-${epicUuid}`, duration: 10000 },
        );
      } catch {
        // Only an offer; if the Epic can't be read, or a later save's read replaced
        // this one, there's nothing to offer here.
      }
    },
    [queryClient, markEpicDone],
  );
};
